import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Grid,
  Autocomplete,
  TextField,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/common/Navbar";
import ViewData from "../../components/grids/ViewDatagrid";
import ListSearchBar from "../../components/common/listSearch";
import { Notelistdata } from "../../assets/Griddata";
import { useStyles } from "../../assets/styles/Theme";
import { externalgetApi } from "../../services/external/externalApis";
import { ORDER_API_14 } from "../../api/ApiModel";
import {
  CON_ORTC_RETURN,
  CON_ORTC_EXCHANGE,
  CON_STATUS_DELETED,
} from "../../utils/sys/Constants";
import { getNoteRefundStatus } from "../../utils/order/noteRefundStatus";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Note list shows only Return (Credit Note) and Exchange note types
const NOTE_TYPE_LIST = [
  { code: "X",              label: "All"      },
  { code: CON_ORTC_RETURN,  label: "Credit Note"   },
  { code: CON_ORTC_EXCHANGE,label: "Exchange Note"  },
];

const safeStr = (val) => {
  if (val == null || typeof val === "object") return "";
  return String(val);
};

const safeNum = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

function NoteList() {
  const [noteData,       setNoteData]       = useState([]);
  const [filteredData,   setFilteredData]   = useState([]);
  const [isLoading,      setIsLoading]      = useState(false);
  const [error,          setError]          = useState(null);
  const [fromDate,       setFromDate]       = useState(dayjs().startOf("month"));
  const [toDate,         setToDate]         = useState(dayjs().endOf("month"));
  const [selectedType,   setSelectedType]   = useState(NOTE_TYPE_LIST[0]);
  const [searchText,     setSearchText]     = useState("");

  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE } = SYS_USER.USER_PARAM || {};

  const navigate = useNavigate();
  const theme    = useTheme();
  const styles   = useStyles(theme);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchNotes = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await externalgetApi(ORDER_API_14, "GET", {
        COMPCODE,
        LOCCODE,
        RESTCODE,
        FROMDATE: dayjs(fromDate).format("MM-DD-YYYY"),
        TODATE:   dayjs(toDate).format("MM-DD-YYYY"),
        STSD:     CON_STATUS_DELETED,
      });
      // Keep only Return (credit note) and Exchange note types
      const notes = (response || []).filter((r) => {
        const code = Number(r.RETURNTYPECODE);
        return code === CON_ORTC_RETURN || code === CON_ORTC_EXCHANGE;
      });
      setNoteData(notes);
      setFilteredData(notes);
    } catch (err) {
      setError(err?.message || "Failed to load notes");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  // ── Client-side filter ─────────────────────────────────────────────────────
  useEffect(() => {
    let result = noteData;

    if (selectedType.code !== "X") {
      result = result.filter(
        (r) => Number(r.RETURNTYPECODE) === Number(selectedType.code)
      );
    }

    if (searchText) {
      const lower = String(searchText).toLowerCase();
      result = result.filter((r) =>
        [
          r.ODRETURNCODE,
          r.ORDERCODE,
          r.INVNO,
          r.RETURNNO,
          r.REMARK,
          r.REFUNDAMOUNT,
        ].some((field) => safeStr(field).toLowerCase().includes(lower))
      );
    }

    setFilteredData(result);
  }, [searchText, noteData, selectedType]);

  // ── Row mapper ─────────────────────────────────────────────────────────────
  const mapRows = (records) =>
    records.map((r) => {
      const refundStatus = getNoteRefundStatus(r);
      const expiryDate = (() => {
        const base = dayjs(r.RETURNDATE);
        if (!base.isValid()) return "";
        return base.add(30, "day").format("YYYY-MM-DD");
      })();

      return {
        ODRETURNCODE:          safeStr(r.ODRETURNCODE),
        RETURNNO:              safeStr(r.RETURNNO),
        INVNO:                 safeStr(r.INVNO),
        EXPIRYDATE:            expiryDate,
        REFUNDAMOUNT: safeNum(r.REFUNDAMOUNT),
        REFUNDSTS: (
          <Chip label={refundStatus.label} color={refundStatus.color} size="small" />
        ),
        REMARK: safeStr(r.REMARK),
      };
    });

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handleViewClick = (ODRETURNCODE) => {
    const record = noteData.find(
      (r) => String(r.ODRETURNCODE) === String(ODRETURNCODE)
    );
    if (record) {
      navigate("/noteview", { state: { noteRecord: record } });
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleFromDateChange  = (v) => { if (v) setFromDate(v); };
  const handleToDateChange    = (v) => { if (v) setToDate(v); };
  const handleTypeChange      = (_, v) => { if (v) setSelectedType(v); };
  const handleSearchChange    = (e) => setSearchText(e.target.value);

  const menuItems = [
    {
      text: "View",
      icon: <VisibilityIcon fontSize="small" />,
      onClick: (id) => handleViewClick(id),
    },
  ];

  return (
    <>
      <Navbar />
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer1}>
          <Breadcrumbs sx={styles.AI_breadcrumbs2}>
            <Link underline="hover" color="inherit" href="/">Home</Link>
            <Link underline="hover" color="inherit">Returns</Link>
            <Link underline="hover" color="text.primary">Note List</Link>
          </Breadcrumbs>
        </Box>
      </AppBar>

      <Box sx={styles.OL_AddStyledBox}>
        <Grid container spacing={2} mr={15} alignItems="center" sx={styles.OL_AddStyledBox_container}>
          {/* From Date */}
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={handleFromDateChange}
                inputFormat="MM/DD/YYYY"
                slotProps={{
                  textField: {
                    sx: {
                      width: "100%",
                      "& .MuiInputBase-root": { height: "40px" },
                      "& .MuiInputAdornment-root .MuiIconButton-root": {
                        color: theme.palette.primary.main,
                      },
                    },
                  },
                  popper: {
                    sx: { "& .MuiPaper-root": { backgroundColor: theme.palette.background.paper, boxShadow: "none" } },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* To Date */}
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={handleToDateChange}
                inputFormat="MM/DD/YYYY"
                slotProps={{
                  textField: {
                    sx: {
                      width: "100%",
                      "& .MuiInputBase-root": { height: "40px" },
                      "& .MuiInputAdornment-root .MuiIconButton-root": {
                        color: theme.palette.primary.main,
                      },
                    },
                  },
                  popper: {
                    sx: { "& .MuiPaper-root": { backgroundColor: theme.palette.background.paper, boxShadow: "none" } },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Note Type */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={NOTE_TYPE_LIST}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              value={selectedType}
              disableClearable
              onChange={handleTypeChange}
              renderInput={(params) => (
                <TextField {...params} label="Note Type" size="small" fullWidth />
              )}
            />
          </Grid>

          {/* Search */}
          <Grid item xs={12} md={5}>
            <ListSearchBar onChange={handleSearchChange} value={searchText} />
          </Grid>
        </Grid>
      </Box>

      <Box sx={styles.OL_vdg}>
        {isLoading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <ViewData
            columns={Notelistdata.columns}
            rows={mapRows(filteredData)}
            menuItems={menuItems}
            tabletitle={Notelistdata.name}
            gridid="ODRETURNCODE"
          />
        )}
      </Box>
    </>
  );
}

export default NoteList;
